import { Button, DialogContent, DialogTitle, MenuItem, Select } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { DocumentIdentifier } from "../../../api/types/documentCollectionTypes";
import {
  getFileAccessOptions,
  type DocumentAccessOption,
} from "./document-collection/documents-view/documentAccessHelper";

interface Props {
  selectedDocuments: DocumentIdentifier[];
  currentDocumentsAccess: DocumentAccessOption[];
  onClose: () => void;
  onApply: () => void;
}

const changeDocumentsAccess = withErrorHandling(adminApi.changeDocumentCollectionDocumentsAccess);

const ChangeDocumentAccessDialog = ({ selectedDocuments, currentDocumentsAccess, onClose, onApply }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [saving, setSaving] = useState(false);

  const [selectedAccess, setSelectedAccess] = useState<DocumentAccessOption>(
    currentDocumentsAccess[0] ?? "ViewAndDownload"
  );

  const isDirty = currentDocumentsAccess.some((docAccess) => docAccess !== selectedAccess);

  const handleSave = async () => {
    if (!isDirty) {
      return;
    }

    setSaving(true);
    const [resp, error] = await changeDocumentsAccess({
      selectedDocuments,
      accessOptions: getFileAccessOptions(selectedAccess),
    });

    setSaving(false);
    if (error) {
      logError(error, "[ChangeDocumentAccessDialog] changeDocumentCollectionDocumentsAccess");
      sendNotificationError(error.message);
      return;
    }

    if (resp.numberOfUpdatedDocuments > 0) {
      sendNotification(`File access updated successfully for ${resp.numberOfUpdatedDocuments} document(s)`);
      onApply();
    } else {
      sendNotification("File access is already up to date. No documents were updated.");
    }

    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>File Access</DialogTitle>
      <DialogCloseButton onClick={onClose} />

      <DialogContent>
        <Select<DocumentAccessOption>
          fullWidth
          value={selectedAccess}
          onChange={(e) => setSelectedAccess(e.target.value as DocumentAccessOption)}
        >
          <MenuItem value="ViewAndDownload">View & Download</MenuItem>
          <MenuItem value="ViewOnly">View</MenuItem>
        </Select>
      </DialogContent>

      <DialogButtons onCancel={onClose}>
        <Button variant="contained" color="primary" loading={saving} onClick={handleSave} disabled={!isDirty}>
          Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default ChangeDocumentAccessDialog;
